import React, { useMemo, useState } from 'react';
import { RoleComponent, UiComponent } from 'components';
import { OrganizationQuery, RoleQuery } from 'queries';
import { useIsMobile } from 'hooks';
import useModal from 'hooks/useModal';
import { theme } from 'styles';
import { IRole } from 'types';

function List() {
  const isMobile = useIsMobile();
  const { handleModal } = useModal();
  const { data: orgs = [] } = OrganizationQuery.useGetAll();
  const { data: roles = [] } = RoleQuery.useGetAllScopes();

  const [orgFilter, setOrgFilter] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const orgName = (orgId: number) =>
    orgs.find((o) => o.id === orgId)?.displayName ?? `조직 ${orgId}`;

  const filtered = useMemo(() => {
    if (!orgFilter) return roles;
    const orgId = Number(orgFilter);
    return roles.filter((r) => r.organizationId === orgId);
  }, [roles, orgFilter]);

  const selected = roles.find((r) => r.id === selectedId) ?? null;

  const openForm = () =>
    handleModal(
      '역할 등록',
      <RoleComponent.Form onCreated={(id) => setSelectedId(id)} />,
      <></>,
    );

  return (
    <>
      <UiComponent.PageTitle>권한 관리</UiComponent.PageTitle>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 3fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <UiComponent.Box>
          <div css={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <select
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
              css={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${theme.color.border}`,
                borderRadius: 4,
              }}
            >
              <option value="">전체 조직</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.displayName}
                </option>
              ))}
            </select>
            <UiComponent.Button
              variant="border"
              color="primary"
              onClick={openForm}
            >
              역할 등록
            </UiComponent.Button>
            <ul css={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filtered.map((r) => (
                <RoleItem
                  key={r.id}
                  role={r}
                  scopeLabel={orgName(r.organizationId)}
                  active={r.id === selectedId}
                  onClick={() => setSelectedId(r.id)}
                />
              ))}
              {filtered.length === 0 && (
                <li css={{ fontSize: 13, color: theme.color.textMuted }}>
                  등록된 역할이 없습니다
                </li>
              )}
            </ul>
          </div>
        </UiComponent.Box>
        <UiComponent.Box>
          {selected ? (
            <RoleComponent.Permissions
              key={selected.id}
              role={selected}
              scopeLabel={orgName(selected.organizationId)}
            />
          ) : (
            <p css={{ color: theme.color.textMuted }}>
              왼쪽에서 역할을 선택해주세요
            </p>
          )}
        </UiComponent.Box>
      </div>
    </>
  );
}

interface RoleItemProps {
  role: IRole;
  scopeLabel: string;
  active: boolean;
  onClick: () => void;
}

function RoleItem({ role, scopeLabel, active, onClick }: RoleItemProps) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        css={{
          width: '100%',
          textAlign: 'left',
          padding: '10px 12px',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 14,
          background: active ? theme.color.grey75 : 'transparent',
          fontWeight: active ? 600 : 400,
          color: role.disabled ? theme.color.textMuted : theme.color.text,
          ':hover': { background: theme.color.grey75 },
        }}
      >
        {role.name}{' '}
        <span css={{ color: theme.color.textSub, fontSize: 13 }}>
          ({scopeLabel})
        </span>
        {role.disabled && (
          <span css={{ fontSize: 12, marginLeft: 4 }}>· 비활성</span>
        )}
      </button>
    </li>
  );
}

export default List;
